/**
 * CRITICAL: Stores the event's last modified timestamp to detect stale cache
 * When event data changes (tickets regenerated, event recreated), this timestamp changes
 * and we can invalidate stale ticket selections
 */
const EVENT_TIMESTAMP_KEY = (eventId: string) => `eventTimestamp-${eventId}`

export const getSelectedTicketsFromStorage = (eventId: string) => {
    const selectedTickets = JSON.parse(sessionStorage.getItem(`selectedTickets-${eventId}`) || '[]')
    return selectedTickets
}

export const setSelectedTicketsToStorage = (selectedTickets: string[], eventId: string, eventTimestamp?: string) => {
    sessionStorage.setItem(`selectedTickets-${eventId}`, JSON.stringify(selectedTickets))

    // Store event timestamp to detect if event data has changed
    if (eventTimestamp) {
        sessionStorage.setItem(EVENT_TIMESTAMP_KEY(eventId), eventTimestamp)
    }
}

/**
 * CRITICAL: Validates that cached ticket selections are still valid for the current event version
 * Returns true if cache is valid, false if stale (event was modified)
 */
export const isTicketCacheValid = (eventId: string, currentEventTimestamp: string): boolean => {
    const cachedTimestamp = sessionStorage.getItem(EVENT_TIMESTAMP_KEY(eventId))

    if (!cachedTimestamp) {
        // No timestamp cached, assume valid but this is first time
        return true
    }

    // If timestamps don't match, event was modified (tickets regenerated, event recreated)
    return cachedTimestamp === currentEventTimestamp
}

/**
 * CRITICAL: Clears ticket cache when event data has changed
 * Call this when event timestamp doesn't match cached timestamp
 */
export const clearStaleTicketCache = (eventId: string) => {
    sessionStorage.removeItem(`selectedTickets-${eventId}`)
    sessionStorage.removeItem(EVENT_TIMESTAMP_KEY(eventId))
    console.warn(`Cleared stale ticket cache for event ${eventId} - event was modified`)
}

export const removeSelectedTicketsFromStorage = (eventId: string) => {
    sessionStorage.removeItem(`selectedTickets-${eventId}`)
    sessionStorage.removeItem(EVENT_TIMESTAMP_KEY(eventId))
}

export const getSelectedTicketsForReleaseFromStorage = (eventId: string) => {
    const selectedTickets = JSON.parse(sessionStorage.getItem(`selectedTickets-forRelease-${eventId}`) || '[]')
    return selectedTickets
}

export const setSelectedTicketsForReleaseToStorage = (selectedTickets: string[], eventId: string) => {
    sessionStorage.setItem(`selectedTickets-forRelease-${eventId}`, JSON.stringify(selectedTickets))
}

export const removeSelectedTicketsForReleaseFromStorage = (eventId: string) => {
    sessionStorage.removeItem(`selectedTickets-forRelease-${eventId}`)
}

/**
 * Clears all seat and payment related data from sessionStorage.
 * Should be called when user signs in/out to prevent state pollution.
 */
export const clearAllSeatAndPaymentStorage = () => {
    // Clear transaction and payment data
    sessionStorage.removeItem('transaction_id')
    sessionStorage.removeItem('lastPaymentData')

    // Clear all selectedTickets entries
    Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('selectedTickets-') || key.startsWith('lockedSeats_')) {
            sessionStorage.removeItem(key)
        }
    })
}
